import { getIdProfile, formatMm, LEG_COUNT, LIFE_STAGES, STAGE_LABEL } from "@/lib/tick-id";
import { scaleWidthPx } from "@/lib/size-scale";
import { Card } from "@/components/page-shell";
import { ScaleAxis, ScaleDrawing } from "./size-scale";

/**
 * The Lone Star tick's four sizes, drawn to scale against each other.
 *
 * The comparison this replaces drew them at 28/44/72 px — about 1:1.6:2.6 — and named
 * them poppy seed, sesame seed and apple seed. The sourced measurements in species.ts
 * give roughly 1:2.5:7, and shift every comparison down one: the larva is a pinhead, the
 * nymph is the poppy seed, and the unfed adult is the sesame seed. Overstating the
 * smaller stages is the error that matters, because nymphs cause most unnoticed bites.
 *
 * Set as a plate on the same millimetre ruler as the size question in the key, so the
 * two figures agree: each stage lying from the zero mark, a hand-lens inset for the ones
 * too small to see, and a numbered caption beside them.
 */
const SEED_HINTS: Record<string, string> = {
  larva: "about a pinhead",
  nymph: "about a poppy seed",
  adult: "about a sesame seed",
};

function Specimen({
  label,
  size,
  hint,
  legs,
  children,
}: {
  label: string;
  size: string;
  hint: string;
  legs: number;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-4 py-1.5">
      {children}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="font-display text-lg leading-tight text-foreground">{label}</span>
          <span className="tabular text-sm text-foreground">{size}</span>
        </span>
        <span className="block text-xs leading-snug text-muted-foreground">
          {hint}, {legs} legs
        </span>
      </span>
    </li>
  );
}

export function LifeStages({ pxPerMm }: { pxPerMm: number }) {
  const lonestar = getIdProfile("lone-star-tick");
  if (!lonestar) return null;
  const width = scaleWidthPx(pxPerMm);

  return (
    <Card>
      <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
        Life stages, at the size they really are
      </h2>

      <figure className="mt-6 grid gap-x-12 gap-y-5 md:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] md:items-start">
        <div>
          <ul className="divide-y divide-border border-y border-border">
            {LIFE_STAGES.map((stage) => {
              const size = lonestar.stages[stage];
              const mm = size.maxMm ?? size.minMm ?? 1;
              return (
                <Specimen
                  key={stage}
                  label={STAGE_LABEL[stage]}
                  size={formatMm(size)}
                  hint={SEED_HINTS[stage]}
                  legs={LEG_COUNT[stage]}
                >
                  <ScaleDrawing
                    mm={mm}
                    pxPerMm={pxPerMm}
                    width={width}
                    pattern={stage === "adult" ? "single-white-dot" : "plain-no-markings"}
                    legs={LEG_COUNT[stage]}
                  />
                </Specimen>
              );
            })}
            <Specimen
              label="Fed female"
              size={formatMm(lonestar.engorgedFemale)}
              hint="about a pea"
              legs={8}
            >
              <ScaleDrawing
                mm={lonestar.engorgedFemale.maxMm ?? 16}
                pxPerMm={pxPerMm}
                width={width}
                engorged
              />
            </Specimen>
          </ul>
          <div className="mt-1">
            <ScaleAxis pxPerMm={pxPerMm} width={width} />
          </div>
        </div>

        <figcaption className="border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground md:max-w-sm">
          <span className="font-semibold text-foreground">Fig. 2.</span> Lone Star tick, drawn to
          the measurements on its own profile page and laid on a millimetre ruler. Larvae have six
          legs; every later stage has eight. Nymphs cause many bites because they are this small.
          The drawings are true to size once you match your screen to a coin in question 1.
        </figcaption>
      </figure>

      <p className="measure mt-6 text-sm leading-relaxed text-muted-foreground">
        These are simple drawings to help you know what to look for. They cannot replace an expert.
        If you are not sure, seal the tick in a bag and ask your state health department or a
        university extension program.
      </p>
    </Card>
  );
}
